import { useForm, usePage } from "@inertiajs/react";
import { useState, useEffect } from "react";
import { Project } from "@/interfaces/project";
import { ProjectReview } from "@/interfaces/project_review";
import type { SharedProps } from "@/types";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import {
  Field,
  Input,
  Textarea,
  Select,
  Checkbox,
} from "@/components/ui/Field";

// The event name and support channel come from props.program, so these read as
// Boba Bash copy without a second hardcoded event name to go stale.
function quickResponses(programName: string, channel: string | null) {
  return [
    {
      label: "No readme",
      text: "Hi! This needs a proper README before it can be approved - a short description of what the project is, how you built it, and how someone runs or tries it. Add one and ship it again!",
    },
    {
      label: "Double-dipping",
      text: `Hey! It looks like this was submitted to both ${programName} and another YSWS program, which isn't allowed. Ship something original for ${programName} instead${channel ? ` - ask in ${channel} if you'd like a hand picking an idea` : ""}.`,
    },
    {
      label: "Excessive AI usage",
      text: `This looks like it leaned on AI for most of its creation. AI is a fine tool, but a ${programName} ship should be something you're proud of having built - if you use it, review and rework the code so the finished project is genuinely yours, and keep it to a small share of the whole.

Keep going until you have something you'd call your best work, add a couple of features yourself, and ship it again.`,
    },
    {
      label: "Hours don't match",
      text: "The Hackatime hours attached here don't line up with what's in the repo. Double check you've attached the right Hackatime projects, and that the commits match the time logged, then ship it again.",
    },
  ] as const;
}

export default function ReviewForm({
  project,
  review,
  attributableEvents = [],
}: {
  project: Project;
  review?: ProjectReview;
  // HQ only, and empty everywhere else - the picker appears with the list.
  attributableEvents?: { id: number; name: string }[];
}) {
  const { props } = usePage<SharedProps>();
  const isHq = props.user.is_admin;
  const responses = quickResponses(
    props.program.name,
    props.program.support_channel_name,
  );

  const seconds =
    review?.approved_seconds ??
    project.total_seconds - project.approved_seconds;

  const { data, setData, post, patch, reset, processing } = useForm({
    review_type: review?.review_type ?? "comment",
    content: review?.content ?? "",
    admin_content: review?.admin_content ?? "",
    approved_hours: Number((seconds / 3600).toPrecision(4)),
    // When editing, seed from the review's own golden-ticket intent (a held
    // approval hasn't applied it to the project yet); otherwise from the project.
    high_quality: (review
      ? (review.grant_golden_ticket ?? false)
      : (project.high_quality ?? null)) as boolean | null,
    // Which budget these hours pay. Blank means "leave it as it is"; the
    // server ignores it entirely for anyone who isn't HQ.
    event_id: "",
  });
  const [adminOnly, setAdminOnly] = useState(false);
  const [showQuickResponses, setShowQuickResponses] = useState(false);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (data.content || data.admin_content) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [data.content, data.admin_content]);

  function insertQuickResponse(text: string) {
    const field =
      data.review_type === "comment" && adminOnly ? "admin_content" : "content";
    const current = data[field];
    setData(field, current ? `${current}\n\n${text}` : text);
    setShowQuickResponses(false);
  }

  function submitReview(e: React.FormEvent) {
    e.preventDefault();

    if (review?.id) {
      patch(
        review.is_pending_approval
          ? `/projects/${project.id}/pending_approvals/${review.id}`
          : `/projects/${project.id}/reviews/${review.id}`,
      );
    } else {
      post(`/projects/${project.id}/reviews`, { onSuccess: () => reset() });
    }
  }

  const isInternal = data.review_type === "comment" && adminOnly;
  const budgetCents = Math.round(
    data.approved_hours * props.program.budget_per_hour_cents,
  );

  return (
    <form
      className="border-line/20 flex w-full flex-col gap-4 border-t pt-6"
      onSubmit={submitReview}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Verdict">
          <Select
            value={data.review_type}
            onChange={(e) => {
              setData("review_type", e.target.value);

              if (e.target.value !== "approval") {
                setData("high_quality", false);
              }

              if (e.target.value !== "rejection") {
                setShowQuickResponses(false);
              }
            }}
          >
            <option value="comment">Comment</option>
            {project.aasm_state === "submitted" && (
              <>
                <option value="rejection">Send back</option>
                <option value="approval">Approve</option>
              </>
            )}
          </Select>
        </Field>

        {data.review_type === "approval" && (
          <Field
            label="Hours to approve"
            hint={
              project.event_id || data.event_id
                ? `Worth $${(budgetCents / 100).toFixed(2)} of event budget.`
                : "No event attached, so these hours pay a travel grant rather than a city's budget."
            }
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              value={data.approved_hours}
              onChange={(e) =>
                setData("approved_hours", Number(e.target.value))
              }
            />
          </Field>
        )}

        {data.review_type === "approval" && attributableEvents.length > 0 && (
          <Field
            label="Pays into"
            hint="Overrides where the hours land. Leave alone to keep what the ship claimed."
          >
            <Select
              value={data.event_id}
              onChange={(e) => setData("event_id", e.target.value)}
            >
              <option value="">
                {project.event_id ? "Keep current event" : "Travel grant"}
              </option>
              <option value="none">Travel grant (no event)</option>
              {attributableEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-6">
        {data.review_type === "comment" && (
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={adminOnly}
              onChange={(e) => {
                if (adminOnly) {
                  setData("content", data.admin_content);
                  setData("admin_content", "");
                } else {
                  setData("admin_content", data.content);
                  setData("content", "");
                }
                setAdminOnly(e.target.checked);
              }}
            />
            <span className="text-body text-ink">Internal only</span>
          </label>
        )}

        {data.review_type === "approval" && isHq && (
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={data.high_quality || false}
              onChange={(e) => setData("high_quality", e.target.checked)}
            />
            <span className="text-body text-ink">High quality</span>
          </label>
        )}
      </div>

      {data.review_type === "rejection" && (
        <div className="relative">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowQuickResponses(!showQuickResponses)}
          >
            Quick responses {showQuickResponses ? "▲" : "▼"}
          </Button>
          {showQuickResponses && (
            <div className="border-line bg-surface shadow-card absolute z-10 mt-1 flex min-w-56 flex-col border-2">
              {responses.map((response) => (
                <button
                  key={response.label}
                  type="button"
                  className="text-body text-ink hover:bg-surface-alt border-line/20 duration-fast cursor-pointer border-b px-4 py-2 text-left transition-colors last:border-b-0"
                  onClick={() => insertQuickResponse(response.text)}
                >
                  {response.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Field
        label={isInternal ? "Internal note" : "Note to the author"}
        hint={
          isInternal
            ? "Only admins and reviewers see this."
            : "Shown to whoever shipped it."
        }
      >
        <Textarea
          className="h-32 resize-none"
          value={isInternal ? data.admin_content : data.content}
          onChange={(e) => {
            if (isInternal) {
              setData("admin_content", e.target.value);
            } else {
              setData("content", e.target.value);
            }
          }}
          required={data.review_type !== "comment"}
        />
      </Field>

      {data.review_type !== "comment" && (
        <Field label="Justification" hint="Only admins and reviewers see this.">
          <Textarea
            className="h-28 resize-none"
            value={data.admin_content}
            onChange={(e) => setData("admin_content", e.target.value)}
            required
          />
        </Field>
      )}

      {!isHq && data.review_type === "approval" && (
        <div className="border-carrot-orange bg-surface-alt flex flex-col gap-1 border-2 border-dashed p-3">
          <Badge tone="warning">Held for HQ</Badge>
          <p className="text-caption text-ink-muted">
            Your approval is queued for an HQ reviewer to authorize before it
            reaches the author, moves any budget, or goes to Airtable.
          </p>
        </div>
      )}

      <div>
        <Button type="submit" disabled={processing}>
          {review ? "Update review" : "Add review"}
        </Button>
      </div>
    </form>
  );
}
